/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskResultsForAgentRequest,
  ListRiskResultsForAgentSecurity,
} from "../models/operations/listriskresultsforagent.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListResultsForAgentQuery,
  prefetchRiskListResultsForAgent,
  queryKeyRiskListResultsForAgent,
  RiskListResultsForAgentQueryData,
} from "./riskListResultsForAgent.core.js";
export {
  buildRiskListResultsForAgentQuery,
  prefetchRiskListResultsForAgent,
  queryKeyRiskListResultsForAgent,
  type RiskListResultsForAgentQueryData,
};

export type RiskListResultsForAgentQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskResultsForAgent risk
 *
 * @remarks
 * List risk analysis results with the `match` field redacted to an opaque length+sha256-prefix fingerprint. Matches the payload and pagination semantics of listRiskResults. Designed for AI assistant / MCP consumption so secret content (gitleaks captures, presidio entities, prompt-injection payloads) never reaches the model context. For shadow_mcp findings the `match` value — a non-sensitive server URL or command identifier — is passed through verbatim.
 */
export function useRiskListResultsForAgent(
  request?: ListRiskResultsForAgentRequest | undefined,
  security?: ListRiskResultsForAgentSecurity | undefined,
  options?: QueryHookOptions<
    RiskListResultsForAgentQueryData,
    RiskListResultsForAgentQueryError
  >,
): UseQueryResult<
  RiskListResultsForAgentQueryData,
  RiskListResultsForAgentQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListResultsForAgentQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskResultsForAgent risk
 *
 * @remarks
 * List risk analysis results with the `match` field redacted to an opaque length+sha256-prefix fingerprint. Matches the payload and pagination semantics of listRiskResults. Designed for AI assistant / MCP consumption so secret content (gitleaks captures, presidio entities, prompt-injection payloads) never reaches the model context. For shadow_mcp findings the `match` value — a non-sensitive server URL or command identifier — is passed through verbatim.
 */
export function useRiskListResultsForAgentSuspense(
  request?: ListRiskResultsForAgentRequest | undefined,
  security?: ListRiskResultsForAgentSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListResultsForAgentQueryData,
    RiskListResultsForAgentQueryError
  >,
): UseSuspenseQueryResult<
  RiskListResultsForAgentQueryData,
  RiskListResultsForAgentQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListResultsForAgentQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListResultsForAgentData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      policyId?: string | undefined;
      chatId?: string | undefined;
      mcpServerId?: string | undefined;
      category?: string | undefined;
      ruleId?: string | undefined;
      userId?: string | undefined;
      uniqueMatch?: boolean | undefined;
      nonAssistant?: boolean | undefined;
      assistantId?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListResultsForAgentQueryData,
): RiskListResultsForAgentQueryData | undefined {
  const key = queryKeyRiskListResultsForAgent(...queryKeyBase);

  return client.setQueryData<RiskListResultsForAgentQueryData>(key, data);
}

export function invalidateRiskListResultsForAgent(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      policyId?: string | undefined;
      chatId?: string | undefined;
      mcpServerId?: string | undefined;
      category?: string | undefined;
      ruleId?: string | undefined;
      userId?: string | undefined;
      uniqueMatch?: boolean | undefined;
      nonAssistant?: boolean | undefined;
      assistantId?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "listForAgent", ...queryKeyBase],
  });
}

export function invalidateAllRiskListResultsForAgent(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "listForAgent"],
  });
}
